import { X } from "lucide-react";
import { useState } from "react";

import { cellView, TONE_CLASS } from "@/components/batch-test/batchCells";
import {
  FIXED_CONDITIONS,
  modelLabel,
  shortSha,
  specAxes,
  teName,
  teSub,
} from "@/components/batch-test/teSpec";
import { IconButton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchRunAxes, MatrixPage, TeBaseline, TeSpec } from "@/types";
import { compactTime } from "@/utils/format";
import { formatScore } from "@/utils/rubric";
import { formatLatency } from "./latency";
import { type MatrixColumn, cellScore, matrixCell, matrixColumnStats } from "./matrix";
import { costTitle, formatCost, teCost } from "@/components/batch-test/cost";
import { scoreColor } from "./scoreColor";

/** A TC has no human number — the head of its id is what people quote. */
export function tcShort(testCaseId: string): string {
  return testCaseId.slice(0, 8);
}

/**
 * TC × TE for one page (fe/TEST_CASES_RESULT.md §3).
 *
 * Rows are the fixed set, columns the TEs that actually ran — never one
 * that did not, which would mix "not run yet" with "ran, no result". A cell
 * is one final state and nothing more; opening it is Test Result.
 *
 * A column head opens in place into what that TE changed against the
 * deployment, and what it actually sent. Collapsed state is local: it is
 * how the reader is looking, not where they are.
 */
export function MatrixTable({
  page,
  columns,
  baseline,
  selectedTc,
  dimmed,
  onRow,
  onCell,
}: {
  page: MatrixPage;
  columns: MatrixColumn[];
  baseline: TeBaseline | null;
  selectedTc: string | null;
  /** Column keys the TE filters do not match — shown, but faded. */
  dimmed: ReadonlySet<string>;
  onRow: (testCaseId: string) => void;
  onCell: (column: MatrixColumn, testCaseId: string) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-[300px] min-w-[240px] border-b border-border px-3 py-2 text-left text-[11px] font-normal tracking-wide text-muted-foreground">
              TC \ TE
            </th>
            {columns.map((column) => {
              const expanded = !!open[column.key];
              const stats = matrixColumnStats(page, column);
              const cost = teCost(page, column.key);
              return (
                <th
                  key={column.key}
                  className={cn(
                    "min-w-[128px] border-b border-l border-border p-0 align-bottom font-normal",
                    expanded && "shadow-[inset_0_-3px_0_var(--color-primary)]",
                  )}
                >
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => toggle(column.key)}
                    className={cn(
                      "grid w-full gap-0.5 px-3 py-2 text-left hover:bg-muted/60",
                      dimmed.has(column.key) && "opacity-45 hover:opacity-100",
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-[13px] font-medium">
                      <span aria-hidden className="text-muted-foreground">
                        {expanded ? "▾" : "▸"}
                      </span>
                      {column.letter}
                      {column.isBaseline && (
                        <span className="rounded-full border border-primary/40 px-1.5 text-[10px] text-primary">
                          기준
                        </span>
                      )}
                    </span>
                    <span className="truncate text-xs">{teName(column.spec, baseline)}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {teSub(column.spec, baseline)}
                    </span>
                    <span className="mt-1 grid gap-0.5 text-[11px] text-muted-foreground tabular-nums">
                      <span>평균 Score {stats.avg == null ? "—" : formatScore(stats.avg)} · {stats.scored}건</span>
                      <span>평균 Latency {formatLatency(stats.avgLatencyMs)} · {stats.latencyMeasured}건</span>
                      {cost && (
                        <span title={costTitle(cost)}>
                          비용 {formatCost(cost)}
                          {column.runCount > 1 && ` · ${column.runCount}회 합계`}
                        </span>
                      )}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
          {columns
            .filter((column) => open[column.key])
            .map((column) => (
              <tr key={`${column.key}-panel`}>
                <td colSpan={columns.length + 1} className="border-b border-border p-0">
                  <TePanel column={column} baseline={baseline} onClose={() => toggle(column.key)} />
                </td>
              </tr>
            ))}
        </thead>
        <tbody>
          {page.test_cases.map((tc) => {
            const selected = selectedTc === tc.test_case_id;
            return (
              <tr key={tc.test_case_id} className="border-b border-border last:border-b-0">
                <th
                  scope="row"
                  aria-current={selected || undefined}
                  className={cn(
                    "p-0 text-left font-normal",
                    selected && "bg-muted shadow-[inset_3px_0_0_var(--color-primary)]",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onRow(tc.test_case_id)}
                    className="grid w-full grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
                  >
                    <span className="text-[11px] text-muted-foreground" title={tc.test_case_id}>
                      {tcShort(tc.test_case_id)}
                    </span>
                    <span className={cn("truncate text-[13px]", !tc.is_active && "opacity-55")}>
                      {tc.trigger_preview || tc.title || "—"}
                    </span>
                    {/* Domain slot — BE-DOMAIN fills it (TEST_CASES_ALL §3.2). */}
                    <span className="text-[11px] text-muted-foreground">—</span>
                  </button>
                </th>
                {columns.map((column) => {
                  const cell = matrixCell(page, column, tc.test_case_id);
                  const view = cellView(cell);
                  const score = cellScore(cell);
                  return (
                    <td key={column.key} className="border-l border-border p-1">
                      <button
                        type="button"
                        aria-label={`${tcShort(tc.test_case_id)} × ${column.letter}`}
                        title={view.title}
                        // No item means the TC was not in that run — nothing to open.
                        disabled={view.tone === "none"}
                        onClick={() => onCell(column, tc.test_case_id)}
                        style={score == null ? undefined : scoreColor(score)}
                        className={cn(
                          "min-h-11 w-full rounded-md px-2 text-center tabular-nums hover:brightness-95 disabled:cursor-default",
                          /^\d/.test(view.text) ? "text-[15px]" : "text-[11px]",
                          score == null && TONE_CLASS[view.tone],
                          dimmed.has(column.key) && "opacity-45",
                        )}
                      >
                        {view.text}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const AXIS_LABELS: [keyof BatchRunAxes, string][] = [
  ["llm_model", "llm_model"],
  ["model_tier", "model_tier"],
  ["reasoning_effort", "reasoning_effort"],
  ["git_ref", "git_ref"],
  ["code_resolution", "code_resolution"],
  ["snapshot_at", "snapshot_at"],
  ["history_limit", "history_limit"],
  ["force_response", "force_response"],
];

function axisValue(key: keyof BatchRunAxes, value: BatchRunAxes[keyof BatchRunAxes]): string {
  if (value == null) return "지정 안 함";
  if (key === "snapshot_at") return compactTime(value);
  if (key === "git_ref") return shortSha(String(value));
  return String(value);
}

function unchanged(spec: TeSpec, baseline: TeBaseline | null): string[] {
  const out: string[] = [];
  if (!spec.model) out.push(modelLabel(baseline?.default_model, baseline));
  if (!spec.code) out.push(`${shortSha(baseline?.deployed_sha)} (배포본)`);
  if (spec.history == null) out.push(`history ${baseline?.history_limit ?? 50}`);
  if (spec.force == null) out.push(`force_response ${String(baseline?.force_response ?? false)}`);
  return out;
}

/**
 * What a column is. The diff first — that is what makes it a different
 * column — then the eight values Replay Lab actually received (§3.2: the
 * expanded head shows what ran, never the spec alone), then what no TE can
 * change, so a surprising score has somewhere to be looked up.
 */
function TePanel({
  column,
  baseline,
  onClose,
}: {
  column: MatrixColumn;
  baseline: TeBaseline | null;
  onClose: () => void;
}) {
  const axes = specAxes(column.spec, baseline);
  const rest = unchanged(column.spec, baseline);
  return (
    <div className="grid gap-2 border-l-[3px] border-primary bg-muted/40 px-4 py-3 text-[13px]">
      <div className="flex items-center gap-2">
        <span className="font-medium">
          {column.letter} {teName(column.spec, baseline)}
        </span>
        {column.isBaseline && (
          <span className="rounded-full border border-primary/40 px-1.5 text-[10px] text-primary">기준</span>
        )}
        {column.runCount > 1 && (
          <span className="text-xs text-muted-foreground">
            이 TE 로 {column.runCount} 회 실행 · 가장 최근 완료분을 표시
          </span>
        )}
        <span className="ml-auto">
          <IconButton label="접기" onClick={onClose}>
            <X />
          </IconButton>
        </span>
      </div>

      {axes.length === 0 ? (
        <p className="text-muted-foreground">
          배포본 그대로입니다 — 바꾼 축이 없습니다.
          {column.isBaseline && " 다른 열은 모두 이 열과의 차이입니다."}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">배포본과 다른 항목 {axes.length} 개</p>
          <dl className="grid grid-cols-[repeat(auto-fit,minmax(168px,1fr))] gap-2">
            {axes.map((axis) => (
              <div key={axis.label} className="grid gap-0.5">
                <dt className="text-[11px] text-muted-foreground">{axis.label}</dt>
                <dd>
                  <span className="text-muted-foreground line-through">{axis.from}</span>
                  <span className="px-1 text-muted-foreground">→</span>
                  <span className="font-medium text-primary">{axis.to}</span>
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
      {rest.length > 0 && (
        <p className="text-xs text-muted-foreground">나머지는 배포본과 같음 — {rest.join(" · ")}</p>
      )}

      <p className="text-xs text-muted-foreground">
        보낸 값 —{" "}
        {AXIS_LABELS.map(([key, label]) => `${label} ${axisValue(key, column.run.axes[key])}`).join(" · ")}
      </p>

      <div className="rounded-md border border-border bg-card px-3 py-2 text-xs">
        <p className="mb-1 font-medium">어느 TE 로도 바꿀 수 없는 조건</p>
        <ul className="grid gap-0.5 text-muted-foreground">
          {FIXED_CONDITIONS.map(([name, why]) => (
            <li key={name}>
              <span className="text-foreground">{name}</span> — {why}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
